import { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";


const EventDetailsTab = ({
  dayjs,
  orgTz,
  isMobile,
  showTicketing,
  name,
  setName,
  description,
  setDescription,
  start,
  setStart,
  end,
  setEnd,
  imageFile,
  imagePreview,
  onImageChange,
  event,
  listed,
  setListed,
  venueName,
  setVenueName,
  venueStreetAddress,
  setVenueStreetAddress,
  venueCity,
  setVenueCity,
  venueRegion,
  setVenueRegion,
  venuePostalCode,
  setVenuePostalCode,
  eventType,
  setEventType,
  pairingEventType,
  setPairingEventType,
  questionGroup,
  setQuestionGroup,
  questionGroups,
  deployments,
  deployment,
  setDeployment,
  registrationLink,
  setRegistrationLink,
  capacity,
  setCapacity,
  // League-specific props
  sportType,
  setSportType,
  leagueFields,
  setLeagueFields,
  maxTeamSize,
  setMaxTeamSize,
  standingsMode,
  setStandingsMode,
  leaguesEnabled,
  onRequestConvertToLeague,
}) => {
  const [disableEdit, setDisableEdit] = useState(false);
  const [source, setSource] = useState("");
  const [newFieldInput, setNewFieldInput] = useState("");


  useEffect(() => {
    try {
      if (event && event?.source !== "tnfm") {
        setDisableEdit(true);
        setSource(
          event?.source.charAt(0).toUpperCase() + event.source.slice(1)
        );
      }
    } catch (e) {
      setDisableEdit(true);
      console.error(e.message);
    }
  }, [event]);

  return (
    <>
      {/* Visibility toggle */}
      <Grid item sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!listed}
              onChange={(e) => setListed(e.target.checked)}
            />
          }
          label={
            listed
              ? "Listed (visible to buyers)"
              : "Unlisted (hidden from buyers)"
          }
        />
      </Grid>
      {disableEdit ? (
        <div style={{ padding: 20 }}>
          <Typography style={{ color: "red" }}>
            This event was created in {source}. You must edit the event's
            details there and they will sync here!
          </Typography>
          <Typography>
            You can still add tickets in the ticketing tab (above)
          </Typography>
        </div>
      ) : null}

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Event Details
        </Typography>
        {/* Event details */}
        <Grid item sx={{ mb: 3 }}>
          <FormControl style={{ width: isMobile ? "100%" : "50%" }}>
            <InputLabel id="event-type-label">Site</InputLabel>
            <Select
              labelId="deployment-label"
              value={deployment?._id || ""}
              label="Site"
              onChange={(e) => setDeployment(e.target.value)}
              fullWidth
              disabled={disableEdit}
            >
              {deployments.map((d) => {
                return (
                  <MenuItem key={d._id} value={d._id}>
                    {d.siteUrl}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item sx={{ mb: 3 }}>
          <TextField
            style={{ width: isMobile ? "100%" : "50%" }}
            label="Event Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disableEdit}
          />
        </Grid>

        <Grid item sx={{ mb: 3 }}>
          <TextField
            label="Description *"
            multiline
            minRows={3}
            style={{ width: isMobile ? "100%" : "50%" }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 140 }}
            disabled={disableEdit}
          />
          <Typography
            variant="caption"
            style={{ display: "block", marginTop: 4 }}
          >
            Maximum 140 characters&nbsp;{description.length}/140
          </Typography>
        </Grid>

        <Grid item sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Event Start Date/Time"
              value={start}
              onChange={(v) => setStart(v)}
              renderInput={(params) => (
                <TextField {...params} fullWidth sx={{ width: "100%" }} />
              )}
              disabled={disableEdit}
              minDate={dayjs().tz(orgTz)}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Event End Date/Time"
              value={end}
              onChange={(v) => setEnd(v)}
              renderInput={(params) => (
                <TextField {...params} fullWidth sx={{ width: "100%" }} />
              )}
              disabled={disableEdit}
                                  minDate={dayjs().tz(orgTz)}

            />
          </LocalizationProvider>
        </Grid>
        {showTicketing ? null : (
          <Grid item sx={{ mb: 3 }}>
            <TextField
              style={{ width: isMobile ? "100%" : "50%" }}
              label="Registration Link*"
              value={registrationLink}
              onChange={(e) => setRegistrationLink(e.target.value)}
              disabled={disableEdit}
            />
            <Typography
              variant="caption"
              style={{ display: "block", marginTop: 4, marginLeft: 5 }}
            >
              Required if you are not selling tickets in the app
            </Typography>
          </Grid>
        )}
        {showTicketing ? null : (
          <Grid item>
            <TextField
              style={{ width: isMobile ? "100%" : "25%" }}
              label="Capacity *"
              type="number"
              inputProps={{ step: 1 }}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              disabled={disableEdit}
            />
          </Grid>
        )}
      </Box>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Event Image
        </Typography>
        {/* Image */}
        <Grid item sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            component="label"
            style={{ width: isMobile ? "100%" : "25%" }}
            disabled={disableEdit}
          >
            {imageFile || event ? "Change Image" : "Upload Image"}
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={onImageChange}
            />
          </Button>
          <Typography
            variant="caption"
            style={{ display: "block", marginTop: 4 }}
          >
            Required image aspect ratio: 2:1 (e.g., 2160x1080, 940x470)
          </Typography>
          {imagePreview ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Preview:
              </Typography>
              <Box
                sx={{
                  width: isMobile ? "100%" : "50%",
                  maxWidth: "100%",
                  height: 320, // fixed preview height box
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  position: "relative",
                  backgroundColor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src={imagePreview}
                  alt="preview"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain", // show entire image without cropping
                    display: "block",
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ mt: 0.5, display: "block", color: "text.secondary" }}
              >
                Image scaled to fit. Recommended aspect ratio ~2:1 for best
                results.
              </Typography>
            </Box>
          ) : null}
        </Grid>
      </Box>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          p: 2,
          mb: 3,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Venue
        </Typography>
        {/* Venue */}
        <Grid item sx={{ mb: 3 }}>
          <TextField
            style={{ width: isMobile ? "100%" : "50%" }}
            label="Venue Name *"
            value={venueName}
            onChange={(e) => setVenueName(e.target.value)}
            disabled={disableEdit}
          />
        </Grid>
        <Grid item sx={{ mb: 3 }}>
          <TextField
            style={{ width: isMobile ? "100%" : "50%" }}
            label="Venue Street Address"
            value={venueStreetAddress}
            onChange={(e) => setVenueStreetAddress(e.target.value)}
            disabled={disableEdit}
          />
        </Grid>
        <Grid item sx={{ mb: 3 }}>
          <TextField
            style={{ width: isMobile ? "100%" : "50%" }}
            label="Venue City"
            value={venueCity}
            onChange={(e) => setVenueCity(e.target.value)}
            disabled={disableEdit}
          />
        </Grid>
        <Grid item sx={{ mb: 3 }}>
          <TextField
            style={{ width: isMobile ? "100%" : "50%" }}
            label="Venue Region/State"
            value={venueRegion}
            onChange={(e) => setVenueRegion(e.target.value)}
            disabled={disableEdit}
          />
        </Grid>
        <Grid item sx={{ mb: 3 }}>
          <TextField
            style={{ width: isMobile ? "100%" : "50%" }}
            label="Venue Postal Code"
            value={venuePostalCode}
            onChange={(e) => setVenuePostalCode(e.target.value)}
            disabled={disableEdit}
          />
        </Grid>
      </Box>

      {event && leaguesEnabled && eventType !== "League" && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Event Configuration
          </Typography>
          <Grid item sx={{ mb: 1 }}>
            <FormControl style={{ width: isMobile ? "100%" : "25%" }}>
              <InputLabel id="event-type-label-existing">Event Type</InputLabel>
              <Select
                labelId="event-type-label-existing"
                value={eventType || "Pairing"}
                label="Event Type"
                onChange={(e) => {
                  if (e.target.value === "League" && onRequestConvertToLeague) {
                    onRequestConvertToLeague();
                  }
                }}
                fullWidth
              >
                <MenuItem value="Pairing">Pairing Event</MenuItem>
                <MenuItem value="League">League</MenuItem>
              </Select>
            </FormControl>
            <Typography
              variant="caption"
              style={{ display: "block", marginTop: 8 }}
            >
              Converting to a League cannot be undone.
            </Typography>
          </Grid>
        </Box>
      )}

      {!event && (
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            p: 2,
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Event Configuration
          </Typography>

          {/* Top-level: Pairing or League */}
          <Grid item sx={{ mb: 3 }}>
            <FormControl style={{ width: isMobile ? "100%" : "25%" }}>
              <InputLabel id="event-type-label">Event Type</InputLabel>
              <Select
                labelId="event-type-label"
                value={eventType}
                label="Event Type"
                onChange={(e) => setEventType(e.target.value)}
                fullWidth
              >
                <MenuItem value="Pairing">Pairing Event</MenuItem>
                {leaguesEnabled && <MenuItem value="League">League</MenuItem>}
              </Select>
            </FormControl>
          </Grid>

          {/* Pairing sub-type */}
          {eventType === "Pairing" && (
            <>
              <Grid item sx={{ mb: 3 }}>
                <FormControl style={{ width: isMobile ? "100%" : "25%" }}>
                  <InputLabel id="pairing-type-label">Pairing Type</InputLabel>
                  <Select
                    labelId="pairing-type-label"
                    value={pairingEventType}
                    label="Pairing Type"
                    onChange={(e) => setPairingEventType(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="hetero">Hetero</MenuItem>
                    <MenuItem value="menseekingmen">Men Seeking Men</MenuItem>
                    <MenuItem value="lesbian">Lesbian</MenuItem>
                    <MenuItem value="lgbtq">LGBTQ</MenuItem>
                    <MenuItem value="openspectrum">Open Spectrum</MenuItem>
                    <MenuItem value="friend">Friend</MenuItem>
                    <MenuItem value="mixer">Mixer</MenuItem>
                    <MenuItem value="group">Group</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item sx={{ mb: 3 }}>
                <FormControl style={{ width: isMobile ? "100%" : "25%" }}>
                  <InputLabel id="question-group-label">Question Group</InputLabel>
                  <Select
                    labelId="question-group-label"
                    value={questionGroup}
                    label="Question Group"
                    onChange={(e) => setQuestionGroup(e.target.value)}
                    fullWidth
                  >
                    {questionGroups.map((group) => (
                      <MenuItem key={group._id} value={group._id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {/* League config */}
          {eventType === "League" && (
            <>
              <Grid item sx={{ mb: 3 }}>
                <TextField
                  label="Sport Type"
                  placeholder="e.g. Kickball, Soccer, Basketball"
                  value={sportType}
                  onChange={(e) => setSportType(e.target.value)}
                  style={{ width: isMobile ? "100%" : "35%" }}
                />
              </Grid>

              <Grid item sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Playing Fields
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                  {leagueFields.map((field, idx) => (
                    <Chip
                      key={idx}
                      label={field}
                      onDelete={() =>
                        setLeagueFields(leagueFields.filter((_, i) => i !== idx))
                      }
                    />
                  ))}
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    size="small"
                    label="Add Field"
                    value={newFieldInput}
                    onChange={(e) => setNewFieldInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newFieldInput.trim()) {
                        setLeagueFields([...leagueFields, newFieldInput.trim()]);
                        setNewFieldInput("");
                      }
                    }}
                    style={{ width: isMobile ? "70%" : "25%" }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (newFieldInput.trim()) {
                        setLeagueFields([...leagueFields, newFieldInput.trim()]);
                        setNewFieldInput("");
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Grid>

              <Grid item sx={{ mb: 3, mt: 2 }}>
                <TextField
                  label="Max Team Size"
                  type="number"
                  inputProps={{ step: 1, min: 1 }}
                  value={maxTeamSize}
                  onChange={(e) => setMaxTeamSize(e.target.value)}
                  style={{ width: isMobile ? "100%" : "15%" }}
                />
              </Grid>

              <Grid item sx={{ mb: 3 }}>
                <FormControl style={{ width: isMobile ? "100%" : "25%" }}>
                  <InputLabel id="standings-mode-label">Standings Mode</InputLabel>
                  <Select
                    labelId="standings-mode-label"
                    value={standingsMode}
                    label="Standings Mode"
                    onChange={(e) => setStandingsMode(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="realtime">Real-time (auto-update on score entry)</MenuItem>
                    <MenuItem value="manual">Manual (organizer publishes when ready)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default EventDetailsTab;
